import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { getCourseWorks } from '../data/coursework';
import { isCanvasStudent, sendApiRequest, thisIsDevEnvironment } from '../lib/helper';
import { assignmentSupplementalFolders } from '../pages/course';
import { IAssignment, ICourseWorkFlat } from '../types/IAssignment';
import { IApiContext } from '../types/Shared';
import { useAccreditationContext } from './accreditationprovider';
import { useAuthenticationContext } from './authenticationprovider';
export const CourseWorksContext = createContext<IApiContext<ICourseWorkFlat[]> & {AccreditorStudentsAssigned?: string[]}>({ Pending: true });
export function useCourseWorkContext() {
    return useContext(CourseWorksContext);
}

export function CourseWorkProvider({ children, assignment, resetFlag }: PropsWithChildren<{ assignment: IAssignment; resetFlag: boolean }>) {

    const accreditationContext = useAccreditationContext()
    const authContext = useAuthenticationContext();
    const [courseworks, setCourseworks] = useState<ICourseWorkFlat[]>();
    const [accreditorStudentsAssigned, setAccreditorStudentsAssigned] = useState<string[] | undefined>();
    const [error, setError] = useState<string | undefined>();
    const [pending, setPending] = useState(true);

    const getUsersWhoOptedOutForPrivacyReasons = (emails: string[]) => {
        return sendApiRequest(`/api/sql/getPrivacySettingsForStudents`, "POST", JSON.stringify({studentEmails: emails})).then((res) => {
            return res;
        }).catch(err => {
            console.error(`Error in checking students for privacy, so will not show student's work`, err);
            return emails;
        });
    }

    useEffect(() => {        
        setError(undefined);
        setPending(true);
        if(!authContext.Pending && authContext.Result)
        {
            getCourseWorks(assignment).then(async courseworks => {
                if(isCanvasStudent(authContext.Result!) || thisIsDevEnvironment)
                {
                    const distinctStudentEmails = courseworks.map(c => c.Metadata.email).filter(e => e).filter((v, i, a) => a.indexOf(v) === i);
                    if(distinctStudentEmails.length > 0)
                    {
                        const studentsWhoOptedOut = await getUsersWhoOptedOutForPrivacyReasons(distinctStudentEmails);
                        let mappedStudents: string[] = studentsWhoOptedOut.map((c: any) => c.ConsentIsRejected);
                        if(mappedStudents.length > 0)
                        {                            
                            const email = thisIsDevEnvironment ? authContext.Result?.KeplerAuthStatus?.account?.username : authContext.Result?.CanvasAuthStatus?.userEmail;
                            mappedStudents = mappedStudents.filter(s => s !== email); //do not hide students coursework from themselves
                            courseworks = courseworks.filter(cw => !mappedStudents.includes(cw.Metadata.email))
                        }
                    }
                }
                if(accreditationContext.Result && !assignmentSupplementalFolders.includes(assignment.Name) && authContext.Result)
                {
                    sendApiRequest(`/api/sql/getListOfStudentsForAccreditorAndCourse?Course=${assignment.Course.SIS || assignment.Course.Course}&AccreditorId=${accreditationContext.Result.AccreditorId}`, "GET").then((students: any[]) => {  
                        setAccreditorStudentsAssigned(students);
                        const studentsAsStringArray: string[] = students.map(s => s.StudentName);
                        if(!authContext.Result?.KeplerAuthStatus?.isKeplerAdministrator)
                        {
                            courseworks = courseworks.filter(cw => studentsAsStringArray.includes(cw.Student));
                        }
                        setCourseworks(courseworks);
                    }).catch(err => {
                        console.error(err);
                        setError("Error getting list of students")
                    });                
                }
                else
                {
                    setCourseworks(courseworks);
                }            
            }).catch(err => {
                setError(err);
            }).finally(() => {
                setPending(false);
            });
        }
    }, [assignment, accreditationContext, resetFlag, authContext.Result, authContext.Pending]);
    return (
        <CourseWorksContext.Provider value={{ Error: error, Pending: pending, Result: courseworks, AccreditorStudentsAssigned: accreditorStudentsAssigned }}>
            {children}
        </CourseWorksContext.Provider>
    );
}